"use client";

import { motion } from "motion/react";
import { Terminal, BarChart3, ShieldCheck, Layers, TrendingUp, Users, Globe } from "lucide-react";
import { useState } from "react";

import AdminView from "./app/admin-view";
import AssetDetailView from "./app/asset-detail-view";
import AuthView from "./app/auth-view";
import DashboardView from "./app/dashboard-view";
import KycView from "./app/kyc-view";
import LayoutShell from "./app/layout-shell";
import MarketplaceView from "./app/marketplace-view";
import PortfolioView from "./app/portfolio-view";
import SettingsView from "./app/settings-view";
import WalletView from "./app/wallet-view";
import { RWALandingHeader, RWALandingFooter } from "./components/landing-ui";
import { ToastProvider } from "./components/ui-feedback";
import { useRWAStore } from "./store/rwa-store";

// The functionality "Proof" container
const ProofApp = ({ onExit }: { onExit: () => void }) => {
    const { isAuthenticated, currentUser, selectedAssetId, selectAsset } = useRWAStore();
    const [activeTab, setActiveTab] = useState("dashboard");

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center p-4">
                <AuthView />
                {/* Back button to landing */}
                <button onClick={onExit} className="absolute top-6 left-6 text-neutral-500 hover:text-white text-sm">
                    ← Return to Site
                </button>
            </div>
        )
    }

    // Route Content Logic
    const renderContent = () => {
        // Force KYC for restricted areas (Marketplace, Portfolio)
        const restrictedTabs = ['marketplace', 'portfolio'];
        if (restrictedTabs.includes(activeTab) && currentUser?.kycStatus !== 'verified') {
            return <KycView />;
        }

        switch (activeTab) {
            case 'dashboard':
                return currentUser?.role === 'admin' ? <AdminView /> : <DashboardView />;
            case 'admin-users':
                return <AdminView />;
            case 'marketplace':
                return <MarketplaceView onAssetClick={(id) => selectAsset(id)} />;
            case 'portfolio':
                return <PortfolioView />;
            case 'wallet':
                return <WalletView />;
            case 'settings':
                return <SettingsView />;
            default:
                return <DashboardView />;
        }
    };

    return (
        <>
            <LayoutShell
                activeTab={activeTab}
                onNavigate={setActiveTab}
                onExit={onExit}
            >
                {renderContent()}
            </LayoutShell>

            {/* Asset Detail Modal */}
            {selectedAssetId && (
                <AssetDetailView
                    assetId={selectedAssetId}
                    onClose={() => selectAsset(null)}
                />
            )}
        </>
    );
};

export default function RWAPortfolioView() {
    const [mode, setMode] = useState<"landing" | "app">("landing");

    if (mode === "app") {
        return (
            <ToastProvider>
                <ProofApp onExit={() => setMode("landing")} />
            </ToastProvider>
        );
    }

    return (
        <div className="min-h-screen bg-neutral-950 text-white selection:bg-emerald-500/30 font-sans">
            <RWALandingHeader onLaunch={() => setMode("app")} />

            {/* --- HERO SECTION --- */}
            <section className="relative h-screen flex flex-col items-center justify-center overflow-hidden pt-16">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,var(--tw-gradient-stops))] from-emerald-900/20 via-neutral-950 to-neutral-950" />

                {/* Floating elements background */}
                <div className="absolute inset-0 overflow-hidden">
                    {[...Array(6)].map((_, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0.1, y: 100 }}
                            animate={{
                                opacity: [0.1, 0.3, 0.1],
                                y: [-20, 20, -20],
                                x: [0, 10, 0]
                            }}
                            transition={{
                                duration: 8 + i * 2,
                                repeat: Infinity,
                                delay: i * 0.5
                            }}
                            className="absolute w-64 h-64 rounded-3xl bg-linear-to-br from-emerald-500/5 to-cyan-500/5 border border-white/5 backdrop-blur-xl"
                            style={{
                                left: `${15 + i * 15}%`,
                                top: `${20 + (i % 3) * 25}%`,
                                transform: `rotate(${i * 15}deg)`
                            }}
                        />
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="relative z-10 text-center max-w-4xl px-4"
                >
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-900/30 border border-emerald-800/50 text-emerald-400 text-sm font-mono mb-8">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                        </span>
                        V3.0 ENTERPRISE
                    </div>

                    <h1 className="text-6xl md:text-8xl font-bold tracking-tighter mb-6 bg-linear-to-b from-white to-neutral-500 bg-clip-text text-transparent">
                        Institutional <br />
                        Asset Management
                    </h1>

                    <p className="text-xl text-neutral-400 mb-10 max-w-2xl mx-auto leading-relaxed">
                        The bridge between Digital Assets and Real World Value.
                        <br />
                        Zero friction. Full compliance. Total control.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
                        <button
                            onClick={() => setMode("app")}
                            className="group relative inline-flex items-center gap-3 px-8 py-4 bg-white text-black rounded-xl font-bold overflow-hidden transition-all hover:bg-neutral-200"
                        >
                            <span className="relative z-10">Launch Terminal</span>
                            <Terminal className="w-4 h-4 relative z-10 transition-transform group-hover:translate-x-1" />
                            <div className="absolute inset-0 bg-linear-to-r from-emerald-500/0 via-emerald-500/10 to-emerald-500/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                        </button>
                        <button className="inline-flex items-center gap-2 px-6 py-4 text-neutral-400 hover:text-white transition-colors font-medium">
                            <Globe className="w-4 h-4" />
                            View Documentation
                        </button>
                    </div>
                </motion.div>

                {/* Scroll indicator */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.5 }}
                    className="absolute bottom-8 left-1/2 -translate-x-1/2"
                >
                    <motion.div
                        animate={{ y: [0, 8, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="w-6 h-10 rounded-full border-2 border-neutral-700 flex items-start justify-center p-2"
                    >
                        <div className="w-1 h-2 bg-neutral-500 rounded-full" />
                    </motion.div>
                </motion.div>
            </section>

            {/* --- STATS BANNER --- */}
            <section className="py-16 border-y border-neutral-900 bg-linear-to-r from-neutral-950 via-neutral-900/50 to-neutral-950">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {[
                            { value: "$2.4B+", label: "Total Value Locked", trend: "+12.5%" },
                            { value: "15,000+", label: "Active Investors", trend: "+8.3%" },
                            { value: "127", label: "Assets Tokenized", trend: "+24" },
                            { value: "$48M", label: "Yield Distributed", trend: "YTD" }
                        ].map((stat, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.1 }}
                                viewport={{ once: true }}
                                className="text-center"
                            >
                                <div className="text-4xl md:text-5xl font-bold font-mono bg-linear-to-r from-white to-neutral-400 bg-clip-text text-transparent mb-2">
                                    {stat.value}
                                </div>
                                <div className="text-neutral-500 text-sm mb-1">{stat.label}</div>
                                <div className="text-emerald-500 text-xs font-mono">{stat.trend}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* --- FEATURED ASSETS PREVIEW --- */}
            <section className="py-24 border-b border-neutral-900">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold mb-4 tracking-tight">Featured Opportunities</h2>
                        <p className="text-neutral-400 max-w-xl mx-auto">Curated investment-grade assets across real estate, fixed income, and alternative investments.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        {[
                            { name: "Manhattan Prime Office", symbol: "NYC-RE", apy: "7.2%", type: "Real Estate", funded: 85, color: "from-blue-500 to-cyan-500" },
                            { name: "Green Energy Infrastructure", symbol: "GREEN", apy: "4.25%", type: "Bond", funded: 91, color: "from-emerald-500 to-teal-500" },
                            { name: "Miami Beachfront Residences", symbol: "MIA-LUX", apy: "8.5%", type: "Real Estate", funded: 75, color: "from-purple-500 to-pink-500" }
                        ].map((asset, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.1 }}
                                viewport={{ once: true }}
                                className="group relative bg-neutral-900/50 border border-neutral-800 rounded-2xl p-6 hover:border-neutral-700 transition-all overflow-hidden"
                            >
                                <div className={`absolute top-0 right-0 w-32 h-32 bg-linear-to-br ${asset.color} opacity-10 blur-3xl group-hover:opacity-20 transition-opacity`} />
                                <div className="relative z-10">
                                    <div className="flex justify-between items-start mb-4">
                                        <span className="text-xs font-mono text-neutral-500 uppercase">{asset.type}</span>
                                        <span className="px-2 py-1 bg-emerald-500/10 text-emerald-500 text-xs font-bold rounded">{asset.apy} APY</span>
                                    </div>
                                    <h3 className="text-xl font-bold mb-1">{asset.name}</h3>
                                    <p className="text-neutral-500 text-sm font-mono mb-4">{asset.symbol}</p>
                                    <div className="space-y-2">
                                        <div className="flex justify-between text-xs">
                                            <span className="text-neutral-500">Funded</span>
                                            <span className="font-mono">{asset.funded}%</span>
                                        </div>
                                        <div className="h-1.5 bg-neutral-800 rounded-full overflow-hidden">
                                            <div className={`h-full bg-linear-to-r ${asset.color}`} style={{ width: `${asset.funded}%` }} />
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <div className="text-center">
                        <button
                            onClick={() => setMode("app")}
                            className="inline-flex items-center gap-2 text-emerald-500 hover:text-emerald-400 font-medium transition-colors"
                        >
                            View All Assets
                            <TrendingUp className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </section>

            {/* --- FEATURE GRID (BUSINESS/PRODUCT) --- */}
            <section id="features" className="py-24 border-b border-neutral-900">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold mb-4 tracking-tight">Enterprise-Grade Infrastructure</h2>
                        <p className="text-neutral-400 max-w-xl mx-auto">Built for institutional requirements with regulatory compliance at every layer.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: BarChart3,
                                title: "Portfolio Analytics",
                                desc: "Real-time PnL tracking across chained and off-chain assets with institutional reporting."
                            },
                            {
                                icon: Layers,
                                title: "Asset Tokenization",
                                desc: "Fractional ownership engine for real estate, commodities, and alternative investments."
                            },
                            {
                                icon: ShieldCheck,
                                title: "Compliance Core",
                                desc: "Automated KYC/AML checks with jurisdiction-aware transfer restrictions."
                            }
                        ].map((feature, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.1 }}
                                viewport={{ once: true }}
                                className="group p-8 rounded-2xl bg-neutral-900/50 border border-neutral-800 hover:border-emerald-500/30 transition-all duration-300"
                            >
                                <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                    <feature.icon className="w-7 h-7 text-emerald-500" />
                                </div>
                                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                                <p className="text-neutral-400 leading-relaxed">{feature.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* --- PARTNERS & INTEGRATIONS --- */}
            <section className="py-24 border-b border-neutral-900">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <span className="text-xs font-mono text-emerald-500 uppercase tracking-widest mb-4 block">Trusted By</span>
                        <h2 className="text-4xl font-bold tracking-tight">Leading Institutions</h2>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 opacity-50">
                        {['Goldman Partners', 'Sequoia Capital', 'Andreessen', 'Blackstone', 'Fidelity', 'Tiger Global'].map((partner, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                transition={{ delay: i * 0.05 }}
                                viewport={{ once: true }}
                                className="flex items-center justify-center py-6"
                            >
                                <div className="text-lg font-bold text-neutral-600 tracking-tight">{partner}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* --- TESTIMONIALS --- */}
            <section className="py-24 border-b border-neutral-900 bg-linear-to-b from-neutral-950 to-neutral-900/30">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold mb-4 tracking-tight">What Institutions Say</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            {
                                quote: "The tokenization infrastructure has transformed how we approach real estate syndication. Settlement times went from weeks to minutes.",
                                author: "Sarah Chen",
                                role: "Managing Director, Alpine Capital",
                                rating: 5
                            },
                            {
                                quote: "Finally, a platform that understands institutional compliance requirements. The automated KYC/AML integration saved us months of development.",
                                author: "Michael Torres",
                                role: "CTO, Meridian Investments",
                                rating: 5
                            },
                            {
                                quote: "Our investors love the transparency. Real-time portfolio tracking and automated yield distributions have significantly improved client satisfaction.",
                                author: "Emma Larsson",
                                role: "Head of Digital Assets, Nordic Wealth",
                                rating: 5
                            }
                        ].map((testimonial, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.1 }}
                                viewport={{ once: true }}
                                className="p-8 rounded-2xl bg-neutral-900/50 border border-neutral-800"
                            >
                                <div className="flex gap-1 mb-4">
                                    {[...Array(testimonial.rating)].map((_, j) => (
                                        <div key={j} className="w-4 h-4 text-amber-500">★</div>
                                    ))}
                                </div>
                                <p className="text-neutral-300 italic mb-6 leading-relaxed">&quot;{testimonial.quote}&quot;</p>
                                <div>
                                    <div className="font-bold">{testimonial.author}</div>
                                    <div className="text-sm text-neutral-500">{testimonial.role}</div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* --- TECHNICAL ARCHITECTURE (ENGINEERING) --- */}
            <section className="py-24 border-b border-neutral-900">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col md:flex-row gap-16 items-center">
                        <div className="flex-1">
                            <span className="text-xs font-mono text-emerald-500 uppercase tracking-widest mb-4 block">For Engineers</span>
                            <h2 className="text-4xl font-bold mb-6 tracking-tight">
                                Engineered for <span className="text-emerald-500">Scale</span>
                            </h2>
                            <ul className="space-y-6">
                                {[
                                    { title: "Next.js App Router", desc: "Server Components for zero-bundle-size SEO with streaming." },
                                    { title: "Zustand State Management", desc: "Atomic updates for high-frequency real-time data." },
                                    { title: "Web3 Simulation Layer", desc: "Complete blockchain mocking without gas fees." },
                                    { title: "Recharts Visualization", desc: "Interactive, performant charts for financial data." }
                                ].map((item, i) => (
                                    <motion.li
                                        key={i}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        transition={{ delay: i * 0.1 }}
                                        viewport={{ once: true }}
                                        className="border-l-2 border-emerald-500/30 pl-6 py-2 hover:border-emerald-500 transition-colors"
                                    >
                                        <h4 className="font-mono text-lg font-bold text-white mb-1">{item.title}</h4>
                                        <p className="text-sm text-neutral-400">{item.desc}</p>
                                    </motion.li>
                                ))}
                            </ul>
                        </div>
                        <div className="flex-1 w-full max-w-lg">
                            <div className="font-mono text-xs text-neutral-500 bg-neutral-950 p-6 rounded-xl border border-neutral-800 leading-tight">
                                <pre>{`
┌─────────────────────────────────────┐
│           RWA Terminal              │
├─────────────────────────────────────┤
│                                     │
│   ┌─────────┐    ┌─────────────┐   │
│   │  Client │◄──►│   Zustand   │   │
│   │   UI    │    │   Store     │   │
│   └────┬────┘    └──────┬──────┘   │
│        │                │          │
│        ▼                ▼          │
│   ┌─────────┐    ┌─────────────┐   │
│   │  Route  │    │  Validation │   │
│   │ Handlers│    │   Layer     │   │
│   └────┬────┘    └─────────────┘   │
│        │                           │
│        ▼                           │
│   ┌─────────────────────────┐      │
│   │    Simulated Database   │      │
│   │    (localStorage)       │      │
│   └─────────────────────────┘      │
│                                     │
└─────────────────────────────────────┘
                `}</pre>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- FINAL CTA --- */}
            <section className="py-32 relative overflow-hidden">
                <div className="absolute inset-0 bg-linear-to-br from-emerald-900/20 via-neutral-950 to-cyan-900/10" />
                <div className="container mx-auto px-4 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="max-w-3xl mx-auto text-center"
                    >
                        <h2 className="text-5xl md:text-6xl font-bold mb-6 tracking-tight">
                            Ready to Transform Your <br />
                            <span className="bg-linear-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">Asset Strategy?</span>
                        </h2>
                        <p className="text-xl text-neutral-400 mb-10">
                            Experience the future of institutional asset management. <br />
                            No credit card required. Full access to all features.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
                            <button
                                onClick={() => setMode("app")}
                                className="group relative inline-flex items-center gap-3 px-10 py-5 bg-linear-to-r from-emerald-500 to-cyan-500 text-black rounded-xl font-bold text-lg overflow-hidden transition-all hover:shadow-[0_0_40px_rgba(16,185,129,0.4)]"
                            >
                                <span className="relative z-10">Start Free Demo</span>
                                <Terminal className="w-5 h-5 relative z-10" />
                            </button>
                            <button className="inline-flex items-center gap-2 px-6 py-4 text-neutral-400 hover:text-white transition-colors font-medium">
                                <Users className="w-4 h-4" />
                                Contact Sales
                            </button>
                        </div>
                    </motion.div>
                </div>
            </section>

            <RWALandingFooter />
        </div>
    );
}
